"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Zap, Building, Crown, Flame } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LaMaisonDieuPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-orange-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">La Maison Dieu</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-xl p-8 mb-8 border border-orange-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-la-maison-dieu.svg"
                  alt="La Maison Dieu Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-orange-900/50 to-red-900/50 border border-orange-500/30 flex-col items-center justify-center text-orange-300">
                  <span className="text-3xl font-bold mb-2">XVI</span>
                  <span className="text-lg font-semibold">LA MAISON DIEU</span>
                  <span className="text-sm text-center mt-2">The Tower</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XVI</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">La Maison Dieu</h1>
                  <p className="text-xl text-orange-400">Kule / Ani Değişim ve Çalkantı</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde on altıncı kart. Yıkılan kuleyi temsil eder ve ani değişimler,
                çalkantılar ve varolanın yıkılması anlamına gelir. Zorunlu dönüşümlerin habercisidir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Ani Değişim</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Çalkantı</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Hakikat</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Yıkım</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ani Aydınlanma</h3>
                  <p className="text-slate-400 text-sm">Hakikatin açığa çıkması ve ani farkındalık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Zorunlu Değişim</h3>
                  <p className="text-slate-400 text-sm">Eskinin yıkılması ve yeninin doğması için gerekli dönüşüm.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgürleşme</h3>
                  <p className="text-slate-400 text-sm">Kısıtlayıcı yapılardan kurtulma ve yeni başlangıçlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gerçeğin Ortaya Çıkması</h3>
                  <p className="text-slate-400 text-sm">Saklanan şeyler ve gerçeklerin açığa çıkması.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Building className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Değişime Direnç</h3>
                  <p className="text-slate-400 text-sm">Gerekli dönüşümlerden kaçma ve değişimi reddetme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Çalkantı</h3>
                  <p className="text-slate-400 text-sm">Manevi kriz, karmaşıklık ve iç çatışma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geciken Değişim</h3>
                  <p className="text-slate-400 text-sm">Kaçınılmaz değişimin ertelenmesi ve uzatılması.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yıkım Korkusu</h3>
                  <p className="text-slate-400 text-sm">Kayıp korkusu ve eskiye sarılma isteği.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Building className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kule</h3>
              <p className="text-slate-400 text-sm">Ego yapıları, gurur ve sarsılan temeller. Yıkılması gereken eski sistem.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Şimşek</h3>
              <p className="text-slate-400 text-sm">İlahi müdahale, ani aydınlanma ve yüksek güçlerden gelen mesaj.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🧍‍♂️🧍‍♀️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Düşen İnsanlar</h3>
              <p className="text-slate-400 text-sm">Ego ölümün sembolik ifadesi ve eski kimliklerin terkedilmesi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ateş</h3>
              <p className="text-slate-400 text-sm">Dönüştürücü enerji, temizlik ve yeniden doğuşa hazırlık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Düşen Taç</h3>
              <p className="text-slate-400 text-sm">Sahte otoritenin sonu ve gerçek alçakgönüllülüğün başlangıcı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌙</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gece</h3>
              <p className="text-slate-400 text-sm">Bilinçsiz süreçlerin ortaya çıkması ve karanlık geçişlerin ışığı.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkide ani değişiklikler</li>
                <li>• Gerçeklerin ortaya çıkması</li>
                <li>• Eski kalıpların yıkılması</li>
                <li>• Yeni başlangıçlar için temizlik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş değişikliği ve yeni fırsatlar</li>
                <li>• Eski sistemlerin yenilenmesi</li>
                <li>• Beklenmedik gelişmeler</li>
                <li>• Zorunlu adaptasyon</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ego ölümden geçiş</li>
                <li>• Ani farkındalık anları</li>
                <li>• Eski kimlikleri bırakma</li>
                <li>• Manevi uyanış</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Building className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlahi müdahale</li>
                <li>• Manevi temizlenme</li>
                <li>• Yüksek bilinç açılımı</li>
                <li>• Sahte manevi yapıların yıkılması</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-diable"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Le Diable
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-etoile"
            className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            Sonraki: L'Étoile
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}